import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  useColorMode,
  useToast,
  Button,
  useBreakpointValue,
  Text,
  Link,
  HStack,
  IconButton,
  CloseButton,
  Tabs,
  TabList,
  Tab,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Heading,
  Input,
  FormControl,
  Spinner,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { ChevronUpIcon, ArrowUpIcon } from "@chakra-ui/icons";
import { useLocation } from "react-router-dom";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import AuthModal from "../AuthModal";
import ItineraryDetails from "../common/ItineraryDetails";
import usePersistedState from "../usePersistedState";
import axiosInstance from "../../helpers/axiosInstance";
// import profilePicture from "../../assets/images/Ava.png";
import backgroundImage from "../../assets/images/Ava.png";
import { useTranslation } from "../../components/LanguageTranslation/TranslationContext";
import Translate from "../../components/LanguageTranslation/Translate";
import { Helmet } from "react-helmet-async";
import AgeConfirmModal from "../common/AgeConfirmModal";
import ChatTabs from "./ChatTabs";
import { createTripPlan } from "../../api/chat/api";
const MAX_MESSAGES = 5; // Maximum number of messages a non-logged-in user can send

const Chat = () => {
  const { colorMode } = useColorMode();
  const history = useHistory();
  const { translateText } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [conversationName, setConversationName] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [tripPlan, setTripPlan] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState(null);
  const toast = useToast();
  const [activeTab, setActiveTab] = usePersistedState(
    "activeTab",
    "general_conversation"
  );
  const [generalInfo, setGeneralInfo] = useState("");
  const [generalInfoAdded, setGeneralInfoAdded] = usePersistedState(
    "generalInfoAdded",
    false
  );
  const [hasHomeToGoData, setHasHomeToGoData] = useState(false);
  const [showNextTabAnimation, setShowNextTabAnimation] = useState(false);

  const [isSignUpOpen, setSignUpOpen] = usePersistedState(
    "isSignUpOpen",
    false
  );
  const [lastUpdated, setLastUpdated] = useState(null);
  const [messageCount, setMessageCount] = useState(0); // State to hold the message count from backend

  const [ageConfirmed, setAgeConfirmed] = useState(false);
  
  // Warning modal state variables (lifted from ChatHeader)
  const [isWarningOpen, setWarningOpen] = useState(false);
  const [warningText, setWarningText] = useState("");
  const [warningFunction, setWarningFunction] = useState(() => () => {});
  const [warningErrors, setWarningErrors] = useState("");
  const [warningLoading, setWarningLoading] = useState(false);

  const isAuthenticated = !!localStorage.getItem("REACT_TOKEN_AUTH_KEY");

  // If they log in/out, reset confirmation
  useEffect(() => {
    if (isAuthenticated) setAgeConfirmed(true);
  }, [isAuthenticated]);



  const handleAgeConfirm = () => {
    setAgeConfirmed(true);
  };

  // Only show chat UI if they're authenticated or have confirmed age
  const canShowChat = isAuthenticated || ageConfirmed;
  


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialMessage = params.get("message");
    const conversationType = params.get("conversation_type");
    const storedId = localStorage.getItem("conversation_id");
    const source = params.get("source");

    // Set the active tab if conversation_type is provided in URL
    // if (conversationType) {
    //   setActiveTab(conversationType);
    // }

    // wrap in an async so we can await fetchMessages
    (async () => {
      // CASE A: we have both a convId and an initialMessage (from ongoing-trip, manage-itinerary, etc.)
      if (storedId && initialMessage && source !== 'main') {
        setConversationId(storedId);

        // 1️⃣ fetch & display old messages
        const old = await fetchMessages(storedId);

        // 2️⃣ now append the initial message
        if (canSendMessage()) {
          const newMsg = { user_message: initialMessage, bot_message: "" };
          setIsLoading(true);
          const updated = [...old, newMsg];
          setMessages(updated);
          await handleBotResponse(updated);
        } else {
          // your existing "over limit" logic …
        }

      // CASE A-MAIN: we have both a convId and an initialMessage from main page - check for warning
      } else if (storedId && initialMessage && source === 'main') {
        
        // Check if the existing conversation is saved
        try {
          const response = await axiosInstance.get(
            `/chat/conversation/${storedId}`,
            { params: { conversation_type: activeTab } }
          );
          const existingConvName = response.data.data.conversation_name;
          const existingMessages = response.data.data.conversation_data || [];
          
          // If conversation exists but has no name and has messages, show warning
          if (!existingConvName && existingMessages.length > 0) {
            setConversationId(storedId);
            setMessages(existingMessages);
            setWarningOpen(true);
            setWarningText(
              "The current chat is not saved and will be lost. Do you wish to continue?"
            );
            
            setWarningFunction(() => () => {
              // Clear existing conversation and start new one
              startNewConversation();
              // Continue with new chat after warning
              if (canSendMessage()) {
                const newMessage = {
                  user_message: initialMessage,
                  bot_message: "",
                  conversation_type: conversationType || activeTab,
                };
                setIsLoading(true);
                setMessages([newMessage]);
                handleBotResponse([newMessage]);
              } else {
                const isAuthenticated = !!localStorage.getItem(
                  "REACT_TOKEN_AUTH_KEY"
                );
                if (!isAuthenticated) {
                  setSignUpOpen(true);
                } else {
                  setError(
                    "You have exceeded the allowed number of messages. Create an account for more."
                  );
                  toast({
                    title: "Error",
                    description:
                      "You have exceeded the allowed number of messages. Create an account for more.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                  });
                }
              }
            });
          } else {
            // Existing conversation is saved or empty, start new chat
            startNewConversation();
            if (canSendMessage()) {
              const newMessage = {
                user_message: initialMessage,
                bot_message: "",
                conversation_type: conversationType || activeTab,
              };
              setIsLoading(true);
              setMessages([newMessage]);
              handleBotResponse([newMessage]);
            } else {
              const isAuthenticated = !!localStorage.getItem(
                "REACT_TOKEN_AUTH_KEY"
              );
              if (!isAuthenticated) {
                setSignUpOpen(true);
              } else {
                setError(
                  "You have exceeded the allowed number of messages. Create an account for more."
                );
                toast({
                  title: "Error",
                  description:
                    "You have exceeded the allowed number of messages. Create an account for more.",
                  status: "error",
                  duration: 3000,
                  isClosable: true,
                });
              }
            }
          }
        } catch (error) {
          // If error fetching existing conversation, start new chat
          startNewConversation();
          if (canSendMessage()) {
            const newMessage = {
              user_message: initialMessage,
              bot_message: "",
              conversation_type: conversationType || activeTab,
            };
            setIsLoading(true);
            setMessages([newMessage]);
            handleBotResponse([newMessage]);
          } else {
            const isAuthenticated = !!localStorage.getItem(
              "REACT_TOKEN_AUTH_KEY"
            );
            if (!isAuthenticated) {
              setSignUpOpen(true);
            } else {
              setError(
                "You have exceeded the allowed number of messages. Create an account for more."
              );
              toast({
                title: "Error",
                description:
                  "You have exceeded the allowed number of messages. Create an account for more.",
                status: "error",
                duration: 3000,
                isClosable: true,
              });
            }
          }
        }

        // CASE B: only convId → just fetch history
      } else if (storedId) {
        setConversationId(storedId);
        await fetchMessages(storedId);

        // CASE C: only initialMessage → brand-new chat (no storedId)
      } else if (initialMessage) {
        
        // No existing conversation, proceed with new chat
        if (canSendMessage()) {
          const newMessage = {
            user_message: initialMessage,
            bot_message: "",
            conversation_type: conversationType || activeTab,
          };
          setIsLoading(true);
          setMessages([newMessage]);
          handleBotResponse([newMessage]);
        } else {
          const isAuthenticated = !!localStorage.getItem(
            "REACT_TOKEN_AUTH_KEY"
          );
          if (!isAuthenticated) {
            setSignUpOpen(true);
          } else {
            setError(
              "You have exceeded the allowed number of messages. Create an account for more."
            );
            toast({
              title: "Error",
              description:
                "You have exceeded the allowed number of messages. Create an account for more.",
              status: "error",
              duration: 3000,
              isClosable: true,
            });
          }
        }
      }

      // 3) Clear the `message`, `conversation_type`, and `source` params so reloading doesn't re-send
      if (initialMessage || conversationType || source) {
        params.delete("message");
        params.delete("conversation_type");
        params.delete("source");
        history.replace({ search: params.toString() });
      }
    })();
  }, [location.search]);

  const startNewConversation = () => {
    setConversationId("");
    setConversationName("");
    setMessages([]);
    setActiveTab("general_conversation");
    setGeneralInfoAdded(false);
    setGeneralInfo("");
    setShowNextTabAnimation(false);
    localStorage.removeItem("conversation_id");
    localStorage.removeItem("activeTab");
  };

  const saveAndContinue = async (event) => {
    await saveChat(event);
    warningFunction();
    setWarningOpen(false);
  };

  const saveChat = async (event) => {
    event.preventDefault();
    let messageBody = {
      name: event.target.name.value,
    };
    try {
      const response = await axiosInstance.post(
        `/chat/conversation/save/${conversationId}`,
        messageBody
      );
      if (response.status === 200) {
        setConversationName(event.target.name.value);
        toast({
          title: "Chat saved.",
          description: `Your chat was successfully saved.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        setWarningErrors("You already have a chat with this name");
      }
    } catch (error) {
      setWarningErrors("You already have a chat with this name");
    }
  };

  const canSendMessage = () => {
    const isAuthenticated = !!localStorage.getItem("REACT_TOKEN_AUTH_KEY");

    if (!isAuthenticated && messageCount >= MAX_MESSAGES) {
      setSignUpOpen(true);
      return false;
    }

    return true;
  };

  // Auto-send first prompt when chat is rendered directly without URL conditions
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialMessage = params.get("message");
    const conversationType = params.get("conversation_type");
    const storedId = localStorage.getItem("conversation_id");
    const source = params.get("source");

    // Only auto-send if no URL parameters, no stored conversation, and in general tab
    if (!initialMessage && !conversationType && !storedId && !source && activeTab === "general_conversation" && messages.length === 0) {
      // Check if user can send messages
      if (canSendMessage()) {
        // Get translated text for the auto-prompt
        const sendAutoPrompt = async () => {
          try {
            const translatedPrompt = await translateText("Start Planning my trip");
            
            const autoMessage = {
              user_message: translatedPrompt,
              bot_message: "",
              conversation_type: "general_conversation",
            };
            setIsLoading(true);
            setMessages([autoMessage]);
            handleBotResponse([autoMessage]);
          } catch (error) {
            console.error("Translation error:", error);
            // Fallback to English if translation fails
            const autoMessage = {
              user_message: "Start Planning my trip",
              bot_message: "",
              conversation_type: "general_conversation",
            };
            setIsLoading(true);
            setMessages([autoMessage]);
            handleBotResponse([autoMessage]);
          }
        };
        
        sendAutoPrompt();
      }
    }
  }, [location.search, activeTab, messages.length, translateText]);

  useEffect(() => {
    if (!conversationId) return; // nothing to fetch yet
    fetchMessages(conversationId); // same helper you already use
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Effect to detect when next tab button becomes visible for the first time
  useEffect(() => {
    // Only show animation in general tab when next tab becomes unlocked
    if (activeTab === "general_conversation" && generalInfo && Object.keys(generalInfo).length > 0) {
      // Check if transportation tab is unlocked (next tab after general)
      const isTransportationUnlocked = generalInfo && Object.keys(generalInfo).length > 0;
      
      if (isTransportationUnlocked && !showNextTabAnimation) {
        setShowNextTabAnimation(true);
        // Stop animation after 4 seconds
        const timer = setTimeout(() => {
          setShowNextTabAnimation(false);
        }, 4000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [generalInfo, activeTab, showNextTabAnimation]);

  // ➊ ─────────  put this *once* near the top of your component ──────────
  const specialTabs = [
    "transportation_conversation",
    "accommodation_conversation",
    "explore_conversation",
  ];
  const pingedOnce = useRef({
    transportation_conversation: false,
    accommodation_conversation: false,
    explore_conversation: false,
  });
  // ───────────────────────────────────────────────────────────────────────

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axiosInstance.get(
        `/chat/conversation/${conversationId}`,
        { params: { conversation_type: activeTab } } // 🆕 send the tab
      );
      if (response.status === 200) {
        const isAuthenticated = !!localStorage.getItem("REACT_TOKEN_AUTH_KEY");
        if (
          !isAuthenticated &&
          response.data.data.conversation_counter >= MAX_MESSAGES
        ) {
          setSignUpOpen(true);
        }
        // console.log(`Chat fetch responase for ${activeTab}`, response.data.data);

        setGeneralInfoAdded(!!response.data.data.is_general_info_added); // 🆕
        setGeneralInfo(response.data.data.general_info);
        setConversationName(response.data.data.conversation_name);
        setConversationId(response.data.data.conversation_id);
        localStorage.setItem(
          "conversation_id",
          response.data.data.conversation_id
        );
        setMessageCount(response.data.data.conversation_counter); // Set message count from backend

        const fetchedMessages = response.data.data.conversation_data;
        const fetchedTripPlan = response.data.data.trip_plan || null;
        const lastUpdated = response.data.data.last_updated || "";
        const homeToGoResponse =
          response.data.data.home_to_go_api_response || [];
        const outdoorsyResponse =
          response.data.data.outdoorsy_api_response || [];
        const viatorResponse = response.data.data.viator_api_response || [];
        const flightInfo = response.data.data.flight_info || [];

        // Attach the tripPlan and viatorResponse to the last message if they exist
        if (fetchedTripPlan) {
          fetchedMessages[fetchedMessages.length - 1].tripPlan =
            fetchedTripPlan;
        }
        if (viatorResponse.length > 0) {
          fetchedMessages[fetchedMessages.length - 1].viatorResponse =
            viatorResponse;
        }
        if (homeToGoResponse.length > 0) {
          fetchedMessages[fetchedMessages.length - 1].homeToGoResponse =
            homeToGoResponse;
        }
        if (outdoorsyResponse.length > 0) {
          fetchedMessages[fetchedMessages.length - 1].outdoorsyResponse =
            outdoorsyResponse;
        }
        if (flightInfo.length > 0) {
          fetchedMessages[fetchedMessages.length - 1].flightInfo = flightInfo;
        }

        setMessages(fetchedMessages);
        setTripPlan(fetchedTripPlan);
        setLastUpdated(lastUpdated); // Set lastUpdated in state
        
        // Check if any message has homeToGoResponse data
        const hasHomeToGo = response.data.data.general_info?.destinations?.[0]?.home_to_go_api_response?.length > 0 || false; 
        setHasHomeToGoData(hasHomeToGo);

        /* ⚡  NEW  ➜  if tab is special & empty & not pinged → send blank */
        if (
          specialTabs.includes(activeTab) &&
          fetchedMessages.length === 0 &&
          !pingedOnce.current[activeTab]
        ) {
          pingedOnce.current[activeTab] = true; // mark so we never repeat
          setIsLoading(true);
          await axiosInstance.post(`/chat/conversation/${conversationId}`, {
            user_message: "", // 👈 blank message
            conversation_type: activeTab,
          });
          setIsLoading(false);
          // 🔄 re-fetch so the bot's auto-reply shows up
          await fetchMessages(conversationId);
        }

        return fetchedMessages;
      } else {
        console.error("Failed to fetch conversation:", response.data.message);
      }
    } catch (error) {
      setError("An error occurred while trying to fetch the conversation.");
      toast({
        title: "Error",
        description:
          "An error occurred while trying to fetch the conversation.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSendMessage = () => {
    if (message.trim() === "") return;

    if (canSendMessage()) {
      setIsLoading(true);
      const newMessage = {
        user_message: message,
        bot_message: "",
        conversation_type: activeTab,
      };

      setMessage("");
      setMessages([...messages, newMessage]);
      handleBotResponse([...messages, newMessage]);
    } else {
      const isAuthenticated = !!localStorage.getItem("REACT_TOKEN_AUTH_KEY");
      if (!isAuthenticated) {
        setSignUpOpen(true);
      } else {
        setError(
          "You have exceeded the allowed number of messages. Create an account for more."
        );
        toast({
          title: "Error",
          description:
            "You have exceeded the allowed number of messages. Create an account for more.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleBotResponse = async (newMessages) => {
    const messageBody = {
      user_message: newMessages[newMessages.length - 1].user_message,
      conversation_type: activeTab,
    };

    let slowResponseToastId = "slow-response";
    const timeout = setTimeout(() => {
      toast({
        id: slowResponseToastId,
        duration: null,
        // `render` gives you an onClose helper
        render: ({ onClose }) => (
          <Box
            position="relative"
            bg="gray.100"
            color="black"
            px={4}
            py={3}
            borderRadius="md"
            boxShadow="md"
          >
            {/* manual close button */}
            <CloseButton
              size="sm"
              position="absolute"
              right="2"
              top="2"
              onClick={onClose} // closes this toast
            />

            <Text fontWeight="bold" mb={0}>
              Hang tight!
            </Text>
            <Text fontSize="sm" mb={0}>
              We're preparing your travel guide. Just a few more seconds!
            </Text>
          </Box>
        ),
      });
    }, 5000); // trigger after 5 seconds

    try {
      let response;
      // Prefer localStorage conversation_id over state if available
      const localStoredId = localStorage.getItem("conversation_id");
      const activeConversationId = localStoredId || conversationId;

      if (activeConversationId) {
        response = await axiosInstance.post(
          `/chat/conversation/${activeConversationId}`,
          messageBody
        );
      } else {
        response = await axiosInstance.post("/chat/conversation", messageBody);
        if (response.data.success) {
          setConversationId(response.data.data.conversation_id);
          localStorage.setItem(
            "conversation_id",
            response.data.data.conversation_id
          );
        }
      }

      clearTimeout(timeout); // Clear toast timer when response arrives

      // Close the slow response toast if it's still visible
      toast.close(slowResponseToastId);

      // console.log("Chat Response", response.data.data);

      if (response.status === 200) {
        setGeneralInfoAdded(!!response.data.data.is_general_info_added);
        setGeneralInfo(response.data.data.general_info);
        const lastUpdated = response.data.data.last_updated || "";

        // newMessages[newMessages.length - 1].bot_message =
        //   response.data.data.bot_message;
        // inside handleBotResponse, after you have response.data …
        newMessages[newMessages.length - 1] = {
          ...newMessages[newMessages.length - 1],
          bot_message: response.data.data.bot_message,
          conversation_type: activeTab, // 🆕 keep the tag
        };

        if (response.data.data.trip_plan) {
          newMessages[newMessages.length - 1].tripPlan =
            response.data.data.trip_plan;
        }
        if (response.data.data.home_to_go_api_response) {
          newMessages[newMessages.length - 1].homeToGoResponse =
            response.data.data.home_to_go_api_response;
        }
        if (response.data.data.outdoorsy_api_response) {
          newMessages[newMessages.length - 1].outdoorsyResponse =
            response.data.data.outdoorsy_api_response;
        }
        if (response.data.data.flight_info) {
          newMessages[newMessages.length - 1].flightInfo =
            response.data.data.flight_info;
        }
        if (
          response.data.data.viator_api_response &&
          response.data.data.viator_api_response.length > 0
        ) {
          newMessages[newMessages.length - 1].viatorResponse =
            response.data.data.viator_api_response;
        }

        setSuggestions(response.data.data.suggestion_button_list || []);
        setLastUpdated(lastUpdated);
        setMessageCount(response.data.data.conversation_counter);
        setMessages(newMessages);
        
        // Check if any message has homeToGoResponse data
        const hasHomeToGo = newMessages.some(message => 
          message.homeToGoResponse && message.homeToGoResponse.length > 0
        );
        setHasHomeToGoData(hasHomeToGo);

        // 🆕 Improved: Use only bot_message.type for auto empty message
        const botMsg = response.data.data.bot_message;
        let shouldSendEmpty = false;
        if (
          activeTab === "transportation_conversation" &&
          botMsg &&
          botMsg.category === "transportation"
        ) {
          shouldSendEmpty = true;
        } else if (
          activeTab === "accommodation_conversation" &&
          botMsg &&
          botMsg.category === "accommodation"
        ) {
          shouldSendEmpty = true;
        } else if (
          activeTab === "explore_conversation" &&
          botMsg &&
          botMsg.category === "explore"
        ) {
          shouldSendEmpty = true;
        }
        // Do not send for plain text
        if (botMsg && botMsg.type === "text") {
          shouldSendEmpty = false;
        }

        if (shouldSendEmpty) {
          // console.log("Tab-specific data detected (by type), sending empty message automatically");
          setTimeout(async () => {
            if (canSendMessage()) {
              setIsLoading(true);
              try {
                await axiosInstance.post(`/chat/conversation/${activeConversationId}`, {
                  user_message: "",
                  conversation_type: activeTab,
                });
                await fetchMessages(activeConversationId);
              } catch (error) {
                console.error("Error sending automatic empty message:", error);
              } finally {
                setIsLoading(false);
              }
            }
          }, 200);
        }

      } else {
        console.error("Response status not successful:", response.status);
      }
    } catch (err) {
      clearTimeout(timeout); // Ensure timeout is cleared on error
      toast.close(slowResponseToastId);

      let errorMessage = "An error occurred while trying to send a message.";
      if (err.status === 429) {
        setError(
          "You have exceeded the allowed number of messages. You need to wait 1 hour before you can send more."
        );
        errorMessage =
          "You have exceeded the allowed number of messages. You need to wait 1 hour before you can send more.";
      } else {
        setError("An error occurred while trying to send a message.");
      }

      // console.log(err);
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setIsLoading(true);
    if (canSendMessage()) {
      const newMessage = {
        user_message: suggestion,
        bot_message: "",
        conversation_type: activeTab,
      };
      setMessage("");
      setMessages([...messages, newMessage]);
      handleBotResponse([...messages, newMessage]);
    } else {
      const isAuthenticated = !!localStorage.getItem("REACT_TOKEN_AUTH_KEY");
      if (!isAuthenticated) {
        setSignUpOpen(true);
      } else {
        setError(
          "You have exceeded the allowed number of messages. Create an account for more."
        );
        toast({
          title: "Error",
          description:
            "You have exceeded the allowed number of messages. Create an account for more.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleSelectItem = (item, type) => {
    setSelectedItem({ ...item, type });
  };

  const handleCloseDetail = () => {
    setSelectedItem(null);
  };

  const [showFirstBox, setShowFirstBox] = useState(true); // Controls which box is visible on mobile
  const [showItineraryOnMobile, setShowItineraryOnMobile] = useState(false); // Controls showing itinerary on mobile

  const toggleBox = () => setShowFirstBox(!showFirstBox); // Toggles between the two boxes

  const isBaseScreen = useBreakpointValue({ base: true, md: false });

  const handleClose = () => {
    if (isBaseScreen) {
      setShowItineraryOnMobile(false); // Close itinerary and return to chat on mobile
    } else {
      handleCloseDetail(); // Close details and return to the chat view on mobile
    }
  };

  /* ---------- NEW state + ref ---------- */
  const listRef = useRef(null);
  const [showScrollUp, setShowScrollUp] = useState(false);

  /* Show button only after scrolling ~250 px */
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const onScroll = () => setShowScrollUp(el.scrollTop > 250);
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () =>
    listRef.current?.scrollTo({ top: 0, behavior: "smooth" });

  const [translatedTitle, setTranslatedTitle] = useState(conversationName || "NOMAD360");

  // Update translated title when conversation name changes
  useEffect(() => {
    const updateTitle = async () => {
      try {
        const translated = await translateText(conversationName || "NOMAD360");
        setTranslatedTitle(translated);
      } catch (error) {
        console.error("Translation error for title:", error);
        setTranslatedTitle(conversationName || "NOMAD360");
      }
    };
    
    updateTitle();
  }, [conversationName, translateText]);

  /* ② NEW: whenever messages change, scroll to the bottom */
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const doCreateTripPlan = async () => {
    setIsCreating(true);
    // Show create toast immediately when creation starts
    toast({
      title: "Creating Trip Plan",
      description: "Please wait while we create your trip plan...",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
    try {
      const response = await createTripPlan(conversationId);
      if (response.success) {
        fetchMessages(conversationId);
        toast({
          title: "Trip Plan Created!",
          description: response.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to create trip plan",
          description: response.message || "An error occurred.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error?.response?.data?.message ||
          error.message ||
          "An error occurred.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateTripPlan = async () => {
    if (!conversationId) return;

    // If the chat isn't saved yet, open Save Chat modal directly
    if (!conversationName) {
      if (typeof openSaveChatRef.current === "function") {
        // after save, onChatSaved handler will proceed
        openSaveChatRef.current();
      }
      return;
    }

    // Already saved → proceed
    await doCreateTripPlan();
  };

  // Child API: capture a function to open Save Chat modal
  const openSaveChatRef = useRef(null);
  const registerOpenSaveChat = (fn) => {
    openSaveChatRef.current = fn;
  };

  // When chat gets saved via header modal, set name locally and create plan
  const handleChatSaved = (newName) => {
    setConversationName(newName);
    doCreateTripPlan();
  };

  return (
    <>
      <Helmet>
        <title>{translatedTitle}</title>
      </Helmet>
      <AgeConfirmModal isOpen={!canShowChat} onConfirm={handleAgeConfirm} />
      {canShowChat && (
        <Flex
          bg="white"
          w={{ base: "100%", md: "100%" }}
          h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "80vh" }}
        >
          {/* Chat Section */}
          {/* {!isBaseScreen  && ( */}
          <Box
            flex="5"
            w={{ base: "100%", md: "50%", lg: "50%" }}
            h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "100vh" }}
            display={{
              base: showItineraryOnMobile ? "none" : "flex",
              md: "flex",
            }}
            flexDirection="column"
            overflowY="auto"
            ml={0}
            bg={colorMode === "dark" ? "gray.700" : "white"}
          >
            {/* Header Section - Fixed at top */}
            <Box flexShrink={0}>
              <ChatHeader
                conversationId={conversationId}
                conversationName={conversationName}
                fetchMessages={fetchMessages}
                startNewConversation={startNewConversation}
                isWarningOpen={isWarningOpen}
                setWarningOpen={setWarningOpen}
                warningText={warningText}
                setWarningText={setWarningText}
                warningFunction={warningFunction}
                setWarningFunction={setWarningFunction}
                warningErrors={warningErrors}
                setWarningErrors={setWarningErrors}
                warningLoading={warningLoading}
                setWarningLoading={setWarningLoading}
                saveAndContinue={saveAndContinue}
                registerOpenSaveChat={registerOpenSaveChat}
                onChatSaved={handleChatSaved}
              />

              <ChatTabs
                active={activeTab}
                onChange={setActiveTab}
                generalInfoAdded={generalInfoAdded}
                generalInfo={generalInfo}
                hasHomeToGoData={hasHomeToGoData}
              />
            </Box>

            {/* Message List Section - Takes remaining space */}
            <Box
              flex="1"
              overflowY="auto"
              bg={colorMode === "dark" ? "gray.700" : "white"}
            >
              <Box
                ref={listRef}
                display="flex"
                bg={colorMode === "dark" ? "gray.700" : "white"}
                width="100%"
                height="100%"
                boxSizing="border-box"
                p={4}
                overflowX="hidden"
                overflowY="auto"
                sx={{
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                <MessageList
                  messages={messages}
                  handleSelectItem={handleSelectItem}
                  isLoading={isLoading}
                  backgroundImage={backgroundImage}
                  conversationId={conversationId}
                />
              </Box>
            </Box>

            {/* Input Section - Fixed at bottom */}
            <Box flexShrink={0}>
              <MessageInput
                message={message}
                setMessage={setMessage}
                handleSendMessage={handleSendMessage}
                colorMode={colorMode}
                suggestions={suggestions}
                handleSuggestionClick={handleSuggestionClick}
                isLoading={isLoading}
                generalInfoAdded={generalInfoAdded}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                showItineraryOnMobile={showItineraryOnMobile}
                setShowItineraryOnMobile={setShowItineraryOnMobile}
                showScrollUp={showScrollUp}
                scrollToTop={scrollToTop}
                generalInfo={generalInfo}
                hasHomeToGoData={hasHomeToGoData}
                showNextTabAnimation={showNextTabAnimation}
              />
            </Box>

            {/* Footer Section - Fixed at bottom */}
            <Box flexShrink={0}>
              <HStack
                display={{ base: "none", md: "block" }}
                spacing={0}
                align="center"
                justifyContent="center"
                width="100%"
                mt={0}
              >
                <Text fontSize={{ base: "xs", xl: "lg" }} mb={0} textAlign="center">
                  <Translate text="By using this chat, you agree to our" />{" "}
                  <Link
                    fontSize={{ base: "xs", xl: "lg" }}
                    href="/policies/terms-of-service"
                    textDecoration="underline"
                  >
                    <Translate text="Terms of Service" />
                  </Link>{" "}
                  <Translate text="and" />{" "}
                  <Link
                   fontSize={{ base: "xs", xl: "lg" }}
                    href="/policies/privacy-policy"
                    textDecoration="underline"
                  >
                    <Translate text="Privacy Policy" />
                  </Link>
                  .
                </Text>
              </HStack>
              <Box display={{ base: "none", md: "block" }} mx={2} my={1}>
                <Text
                  textAlign="justify"
                  lineHeight="shorter"
                  fontSize={{ base: "xs", md: "10px", lg: "10px", xl: "16px" }}
                  fontWeight="light"
                  color="gray.500"
                  mb={0}
                >
                  <Translate text="Please note: You will communicate with an assistant that uses artificial intelligence (AI). The information you receive may contain errors. We recommend that you always check prices and travel advice via the provider's website and with official authorities before making a booking. NOMAD360™ does not guarantee the accuracy, completeness or suitability of the information provided and expressly excludes any liability for this." />
                </Text>
              </Box>
            </Box>

            <AuthModal
              isOpen={isSignUpOpen}
              onClose={() => setSignUpOpen(false)}
              redirectPath="/ai-travel-planner/chat"
            />
          </Box>
          {/* )} */}

          {/* Mobile Itinerary Details Section */}
          {isBaseScreen && showItineraryOnMobile && (
            <Box
              w="100%"
              h="100vh"
              overflowY="auto"
              backgroundImage={`url(${backgroundImage})`}
              backgroundSize="cover"
              backgroundPosition="top"
              display="flex"
              flexDirection="column"
              borderRadius="xl"
              boxShadow="xl"
            >
              <Box borderRadius="xl" boxShadow="xl" h="100vh" w="100%">
                {generalInfo && Object.keys(generalInfo).length > 0 && (
                  <ItineraryDetails
                    generalInfo={generalInfo}
                    tripId={conversationId}
                    lastUpdated={lastUpdated}
                    onCreateTripPlan={handleCreateTripPlan}
                    isCreating={isCreating}
                    onClose={handleClose}
                    isMobile={true}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Itinerary Details Section */}
          {!isBaseScreen && (
            <Box
              flex="5"
              maxW={{ base: "100vw", md: "50vw", lg: "40vw" }}
              w={{ base: "100%", md: "50%", lg: "50%" }}
              h="100vh"
              overflowY="auto"
              backgroundImage={`url(${backgroundImage})`}
              backgroundSize="cover"
              backgroundPosition="top"
              display="flex"
              flexDirection="column"
              borderLeftRadius="xl"
              boxLeftShadow="xl"
            >
              <Box borderLeftRadius="xl" boxLeftShadow="xl" h="100vh" w="100%">
                {generalInfo && Object.keys(generalInfo).length > 0 && (
                  <ItineraryDetails
                    generalInfo={generalInfo}
                    tripId={conversationId}
                    lastUpdated={lastUpdated}
                    onCreateTripPlan={handleCreateTripPlan}
                    isCreating={isCreating}
                    onClose={handleClose}
                    isMobile={false}
                  />
                )}
              </Box>
            </Box>
          )}
          
        </Flex>
      )}


    </>
  );
};

export default Chat;
